import React from "react";
import { Link } from "react-router-dom";
import { OrDropdown } from "../../atoms/or-dropdown";
import { OrIcon } from "../../atoms/or-icon";
import "./header-business.css";
import { HeaderBusinessProps, useHeaderBusiness } from "./header-business.hook";

export type { HeaderBusinessCompanyOption, HeaderBusinessMenuItem, HeaderBusinessProps } from "./header-business.hook";

export function HeaderBusiness({
  avatarSrc,
  companyName,
  companyOptions = [],
  companyPlaceholder = "Buscar empresa",
  isMobileMenuOpen = false,
  menuItems,
  notificationCount = 0,
  onCompanyChange,
  onMobileMenuToggle,
  onNotificationClick,
  onSupportClick,
  selectedCompanyId,
  showMobileMenuToggle = true,
  supportButtonLabel = "Ayuda y soporte",
}: HeaderBusinessProps): React.ReactElement {
  const { closeMenu, initials, isMenuOpen, menuRef, selectedCompanyOption, toggleMenu, triggerRef } = useHeaderBusiness(
    {
      avatarSrc,
      companyName,
      companyOptions,
      companyPlaceholder,
      isMobileMenuOpen,
      menuItems,
      notificationCount,
      onCompanyChange,
      onMobileMenuToggle,
      onNotificationClick,
      onSupportClick,
      selectedCompanyId,
      showMobileMenuToggle,
      supportButtonLabel,
    },
  );
  const visibleNotificationCount = notificationCount > 99 ? "99+" : `${notificationCount}`;

  return (
    <header className="header-business">
      <div className="header-business__actions">
        {companyOptions.length > 0 && onCompanyChange ? (
          <div className="header-business__selector">
            <OrDropdown
              disableClearable={true}
              getOptionLabel={(option) => option.label}
              id="header-business-company-selector"
              onChange={(_event, nextValue) => {
                if (Array.isArray(nextValue)) {
                  return;
                }

                onCompanyChange(nextValue?.id ?? null);
              }}
              options={companyOptions}
              placeholder={companyPlaceholder}
              value={selectedCompanyOption}
              variant="soft"
            />
          </div>
        ) : null}

        <div className="header-business__meta">
          {showMobileMenuToggle ? (
            <button
              aria-expanded={isMobileMenuOpen}
              aria-label="Abrir menú"
              className="header-business__mobile-toggle"
              onClick={onMobileMenuToggle}
              type="button"
            >
              <OrIcon name="menu" size={24} />
            </button>
          ) : null}

          {onSupportClick ? (
            <button
              aria-label={supportButtonLabel}
              className="header-business__support"
              onClick={onSupportClick}
              type="button"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                <g fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5">
                  <path d="M21 12a9 9 0 1 1-18 0a9 9 0 0 1 18 0" />
                  <path d="M12 13.496c0-2.003 2-1.503 2-3.506c0-2.659-4-2.659-4 0m2 6.007v-.5" />
                </g>
              </svg>
            </button>
          ) : null}

          <button
            aria-label={`Notificaciones ${notificationCount > 0 ? `(${notificationCount})` : ""}`.trim()}
            className="header-business__notification"
            onClick={onNotificationClick}
            type="button"
          >
            <OrIcon name="bell" size={20} />
            {notificationCount > 0 ? (
              <span aria-hidden="true" className="header-business__notification-count">
                {visibleNotificationCount}
              </span>
            ) : null}
          </button>

          <div className="header-business__profile">
            <button
              aria-expanded={isMenuOpen}
              aria-haspopup="menu"
              className="header-business__profile-trigger"
              onClick={toggleMenu}
              ref={(node) => {
                triggerRef.current = node;
              }}
              type="button"
            >
              <span className="header-business__company-name">{companyName}</span>
              {avatarSrc ? (
                <img alt={companyName} className="header-business__avatar" src={avatarSrc} />
              ) : (
                <span aria-hidden="true" className="header-business__avatar header-business__avatar--fallback">
                  {initials}
                </span>
              )}
            </button>

            <div
              className={`header-business__menu ${isMenuOpen ? "header-business__menu--open" : ""}`}
              ref={(node) => {
                menuRef.current = node;
              }}
            >
              <ul className="header-business__menu-list" role="menu">
                {menuItems.map((item) => (
                  <li className="header-business__menu-item" key={item.id} role="none">
                    {item.to && !item.disabled ? (
                      <Link
                        className={`header-business__menu-action ${
                          item.tone === "danger" ? "header-business__menu-action--danger" : ""
                        }`}
                        onClick={() => {
                          item.onSelect?.();
                          closeMenu();
                        }}
                        role="menuitem"
                        to={item.to}
                      >
                        {item.icon ? <span className="header-business__menu-icon">{item.icon}</span> : null}
                        <span>{item.label}</span>
                      </Link>
                    ) : (
                      <button
                        className={`header-business__menu-action ${
                          item.tone === "danger" ? "header-business__menu-action--danger" : ""
                        }`}
                        disabled={item.disabled}
                        onClick={() => {
                          item.onSelect?.();
                          closeMenu();
                        }}
                        role="menuitem"
                        type="button"
                      >
                        {item.icon ? <span className="header-business__menu-icon">{item.icon}</span> : null}
                        <span>{item.label}</span>
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
